import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
    History,
    Calendar,
    ChefHat,
    DollarSign,
    Users,
    Clock,
    ShoppingCart,
    ArrowRight,
    Loader2,
    Sparkles,
    Package,
    X,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

const PersonalPackHistory = ({ onClose, onSelectPack }) => {
    const navigate = useNavigate();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedPack, setExpandedPack] = useState(null);

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const response = await api.getPersonalPacks(10);
            setPacks(response.data);
        } catch (err) {
            console.error('Error loading pack history:', err);
            setError('Erreur lors du chargement de l\'historique');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-CA', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getBudgetLabel = (budgetType) => {
        const labels = {
            tight: 'Fin de mois',
            normal: 'Normal',
            comfortable: 'Confortable'
        };
        return labels[budgetType] || budgetType;
    };

    const getHouseholdLabel = (size) => {
        const labels = {
            solo: 'Solo',
            couple: 'Couple',
            family: 'Famille',
            large_family: 'Grande famille'
        };
        return labels[size] || size;
    };

    const toggleExpand = (packId) => {
        setExpandedPack(expandedPack === packId ? null : packId);
    };

    if (loading) {
        return (
            <Card className="border-2 border-purple-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2">
                        <History className="w-5 h-5" />
                        Historique de mes packs
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-2 border-purple-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <History className="w-5 h-5" />
                        Historique de mes packs
                    </div>
                    {onClose && (
                        <button 
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
                {error ? (
                    <div className="text-center py-8 text-red-500">
                        {error}
                    </div>
                ) : packs.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Package className="w-8 h-8 text-purple-500" />
                        </div>
                        <h3 className="font-bold text-foreground mb-2">Aucun pack généré</h3>
                        <p className="text-muted-foreground text-sm">
                            Crée ton premier pack IA personnalisé !
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                        {packs.map((pack, index) => (
                            <div 
                                key={pack.id || index}
                                className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-purple-300 transition-colors"
                            >
                                {/* Pack Header - Clickable */}
                                <button
                                    onClick={() => toggleExpand(pack.id)}
                                    className="w-full p-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center text-white font-bold">
                                            S{pack.week_number || '?'}
                                        </div>
                                        <div className="text-left">
                                            <div className="font-bold text-foreground line-clamp-1">
                                                {pack.title || 'Pack personnalisé'}
                                            </div>
                                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(pack.generated_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right hidden sm:block">
                                            <div className="font-bold text-green-600">
                                                ~{pack.estimated_total_cost?.toFixed(0) || '?'}$
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {pack.soupers?.length || 5} recettes
                                            </div>
                                        </div>
                                        {expandedPack === pack.id ? (
                                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                        )}
                                    </div>
                                </button>

                                {/* Expanded Content */}
                                {expandedPack === pack.id && (
                                    <div className="border-t-2 border-gray-100 bg-gray-50 p-4">
                                        {/* Criteria Tags */}
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            <Badge variant="secondary" className="gap-1">
                                                <DollarSign className="w-3 h-3" />
                                                {getBudgetLabel(pack.criteria?.budget_type)}
                                            </Badge>
                                            <Badge variant="secondary" className="gap-1">
                                                <Users className="w-3 h-3" />
                                                {getHouseholdLabel(pack.criteria?.household_size)}
                                            </Badge>
                                            <Badge variant="secondary" className="gap-1">
                                                <Clock className="w-3 h-3" />
                                                ≤{pack.criteria?.max_time || 60} min
                                            </Badge>
                                        </div>

                                        {/* Description */}
                                        {pack.description && (
                                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                                {pack.description}
                                            </p>
                                        )}

                                        {/* Recipes List */}
                                        <div className="space-y-2 mb-4">
                                            {pack.soupers?.slice(0, 5).map((souper, idx) => (
                                                <div 
                                                    key={idx}
                                                    className="flex items-center gap-2 p-2 bg-white rounded-lg border cursor-pointer hover:bg-purple-50 transition-colors"
                                                    onClick={() => navigate(`/recipes/${souper.recipe_id}`)}
                                                >
                                                    <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-purple-700 text-xs font-bold">
                                                        {souper.day?.charAt(0) || idx + 1}
                                                    </div>
                                                    {souper.image_url && (
                                                        <img 
                                                            src={souper.image_url} 
                                                            alt=""
                                                            className="w-8 h-8 rounded object-cover"
                                                        />
                                                    )}
                                                    <span className="flex-1 text-sm font-medium truncate">
                                                        {souper.title}
                                                    </span>
                                                    <span className="text-xs text-green-600 font-bold">
                                                        {souper.cost_per_serving?.toFixed(2)}$
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Stats */}
                                        <div className="grid grid-cols-3 gap-2 p-3 bg-white rounded-lg border mb-4">
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-green-600">
                                                    ~{pack.estimated_total_cost?.toFixed(0)}$
                                                </div>
                                                <div className="text-xs text-muted-foreground">Total</div>
                                            </div>
                                            <div className="text-center border-x">
                                                <div className="text-lg font-bold text-blue-600">
                                                    {pack.grocery_list?.length || '?'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">Ingrédients</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-purple-600">
                                                    ~{pack.estimated_cost_per_person?.toFixed(0)}$
                                                </div>
                                                <div className="text-xs text-muted-foreground">/personne</div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        {onSelectPack && (
                                            <Button 
                                                onClick={() => onSelectPack(pack)}
                                                className="w-full gap-2"
                                                variant="outline"
                                            >
                                                <ShoppingCart className="w-4 h-4" />
                                                Voir la liste d'épicerie
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default PersonalPackHistory;
